/**
 * Employee address contract — mirrors `platform.employee_addresses`.
 *
 * Each employee has at most two addresses — a `current` residence and a
 * `permanent` home — enforced at the DB layer by a partial unique index
 * on `(employee_id, address_type)`.
 */
import { z } from 'zod';

import { TenantIdSchema } from '../common/identifiers.js';
import { AuditFieldsSchema } from '../common/timestamps.js';

import { EmployeeIdSchema } from './employee.js';

export const EmployeeAddressIdSchema = z.string().uuid().brand<'EmployeeAddressId'>();
export type EmployeeAddressId = z.infer<typeof EmployeeAddressIdSchema>;

export const AddressTypeSchema = z.enum(['current', 'permanent']);
export type AddressType = z.infer<typeof AddressTypeSchema>;

export const EmployeeAddressSchema = AuditFieldsSchema.extend({
  id: EmployeeAddressIdSchema,
  tenantId: TenantIdSchema,
  employeeId: EmployeeIdSchema,
  addressType: AddressTypeSchema,
  line1: z.string().min(1).max(200),
  line2: z.string().max(200).nullable(),
  city: z.string().min(1).max(100),
  state: z.string().max(100).nullable(),
  country: z.string().min(1).max(100),
  postalCode: z.string().max(32).nullable(),
});
export type EmployeeAddress = z.infer<typeof EmployeeAddressSchema>;

/** Input for `POST /employees/:id/addresses`. */
export const NewEmployeeAddressSchema = z.object({
  addressType: AddressTypeSchema,
  line1: z.string().min(1).max(200),
  line2: z.string().max(200).nullable().optional(),
  city: z.string().min(1).max(100),
  state: z.string().max(100).nullable().optional(),
  country: z.string().min(1).max(100),
  postalCode: z.string().max(32).nullable().optional(),
});
export type NewEmployeeAddress = z.infer<typeof NewEmployeeAddressSchema>;

/** Input for `PATCH /employees/:id/addresses/:addressId`. */
export const UpdateEmployeeAddressSchema = NewEmployeeAddressSchema.partial();
export type UpdateEmployeeAddress = z.infer<typeof UpdateEmployeeAddressSchema>;
